import { FC, useEffect, useState } from 'react'

import { endOfToday, startOfToday } from 'date-fns'

import { REQUEST_COMMENTARY_INTERVAL } from '@sportworld/js-sdk/src/constants/soccerData'
import { MatchItem } from '@sportworld/js-sdk/src/types/soccerDataTypes'
import { formatDateForMatchesRequest } from '@sportworld/js-sdk/src/utils/soccerData'

import { getMatches } from '@/actions'
import { ContentUnavailable } from '@/components/ContentUnavailable/ContentUnavailable'
import { MatchCommentaryContent } from '@/components/DataWidget/components/MatchCommentaryContent'
import { Loader } from '@/components/Loader'
import { Arrow, orientation } from '@/constants/focus'
import { scrollOptions } from '@/constants/scrollers'
import { useCustomFocus, useDataWidgetTeamId } from '@/hooks'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import useStore from '@/store/useStore'

import { commentaryScrollOffset, focusIds } from '../constants'
import {
  CommentaryWrapper,
  ContentUnavailableWrapper,
  MatchesContainer,
  MatchesHorizontalScrollContainer,
  MatchWrapper,
} from '../styles'
import { type DataWidgetPartProps } from '../types'
import { Match } from './Match'

export const Commentary: FC<DataWidgetPartProps> = ({
  contestantId,
  tournamentCalendarId,
  commentaryType,
}) => {
  const { matches, loading, setMatches } = useStore((state) => state.soccerData)
  const [activeMatch, setActiveMatch] = useState<Nullable<MatchItem>>(null)

  const teamId = useDataWidgetTeamId()
  const matchesContainerFocusId =
    scrollOptions.MatchesHorizontalScroll.containerId
  const {
    defaultFocusChild: activeMatchIndex,
    onMove,
    onSelectWithScroll,
  } = useCustomFocus({
    focusId: matchesContainerFocusId,
    scrollOptions: scrollOptions.MatchesHorizontalScroll,
    disablePointer: true,
  })

  const { t } = useCommonTranslation()

  const start = startOfToday()
  const end = endOfToday()

  useEffect(() => {
    void updateMatches()
    const intervalId = setInterval(updateMatches, REQUEST_COMMENTARY_INTERVAL)
    return () => {
      clearInterval(intervalId)
      setMatches([])
    }
  }, [])

  useEffect(() => {
    if (!matches.length) {
      setActiveMatch(null)
      return
    }
    setActiveMatch(matches[activeMatchIndex])
  }, [matches, activeMatchIndex])

  const updateMatches = async () => {
    await getMatches({
      contestantId: contestantId || teamId,
      tournamentCalendarId,
      matchTimeRangeStart: formatDateForMatchesRequest(start),
      matchTimeRangeEnd: formatDateForMatchesRequest(end),
    })
  }

  const renderMatches = () => {
    if (!matches.length && loading.matches) {
      return <Loader />
    }

    if (!loading.matches && !matches.length) {
      return (
        <ContentUnavailableWrapper>
          <ContentUnavailable>
            {t('dataWidget.commentary.noData')}
          </ContentUnavailable>
        </ContentUnavailableWrapper>
      )
    }

    return (
      <MatchesHorizontalScrollContainer
        focusId={matchesContainerFocusId}
        id={matchesContainerFocusId}
        defaultFocusChild={activeMatchIndex}
        onMove={onMove}
        orientation={orientation.HORIZONTAL}
        onSelected={(e: LRUDEvent) => {
          onSelectWithScroll(e.targetNode.focusId, e.node.focusedChildIndex)
        }}
      >
        {matches.map((match: any, i: number) => (
          <MatchWrapper
            key={match.id}
            data-active={(activeMatchIndex === i).toString()}
          >
            <Match match={match} isCommentaryTab />
          </MatchWrapper>
        ))}
      </MatchesHorizontalScrollContainer>
    )
  }

  const onArrowHandler = (e: LRUDEvent) => {
    const container = e.targetNode.elRef.current
    if (e.key === Arrow.DOWN) {
      return container?.scrollTo(
        0,
        container.scrollTop + commentaryScrollOffset,
      )
    }
    if (e.key === Arrow.UP) {
      if (container && container.scrollTop > 0) {
        e.preventDefault()
        e.stopPropagation()
        container.scrollTo(0, container.scrollTop - commentaryScrollOffset)
      }
      return
    }

    let nextIndex
    const maxMatchIndex = matches.length - 1
    if (e.key === Arrow.RIGHT && activeMatchIndex < maxMatchIndex) {
      nextIndex = activeMatchIndex + 1
    }
    if (e.key === Arrow.LEFT && activeMatchIndex > 0) {
      e.preventDefault()
      e.stopPropagation()
      nextIndex = activeMatchIndex - 1
    }
    if (nextIndex === undefined) {
      return
    }
    const matchId = matches[nextIndex]?.id
    const nextFocusId = focusIds.matchCommentary + matchId
    onSelectWithScroll(nextFocusId, nextIndex)
  }

  return (
    <CommentaryWrapper orientation={orientation.VERTICAL}>
      <MatchesContainer>{renderMatches()}</MatchesContainer>
      <MatchCommentaryContent
        matches={matches}
        activeMatch={activeMatch}
        onArrowHandler={onArrowHandler}
        commentaryType={commentaryType}
      />
    </CommentaryWrapper>
  )
}
